'use client';

// useImageUpload - TanStack Query mutation for uploading and analyzing images with GPT-4o vision
// Story 6.5: Added referrerBotId for viral tracking attribution

import { useState, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { FileRejection } from 'react-dropzone';
import { saveSessionToStorage, getOrCreateClientSession } from '../lib/session';
import { getReferral, clearReferral } from '../lib/referralTracking';
import type { UploadedImage, ImageStatus } from '../features/content-input';
import {
  validateImage,
  validateImageCount,
  MAX_IMAGE_COUNT,
  type ImageValidationErrorCode,
} from '../features/content-input/validation';

/**
 * Response from the image upload API
 */
interface ImageUploadResponse {
  data: {
    contentId: string;
    botId: string;
    fileName: string;
    fileSize: number;
    sourceType: 'image';
    thumbnailUrl: string;
    extractedText: string;
    description: string;
    preview: string;
    characterCount?: number;
    status?: 'error';
    errorCode?: string;
  };
  error?: {
    code: string;
    message: string;
  };
}

/**
 * Error response from the image upload API
 */
interface ImageUploadError {
  error: {
    code: string;
    message: string;
  };
}

/**
 * Error codes that can be returned by the image upload process
 */
type ImageErrorCode =
  | ImageValidationErrorCode
  | 'visionFailed'
  | 'storageFailed'
  | 'uploadFailed';

/**
 * Maps API error codes to i18n error keys
 */
function mapErrorCodeToI18nKey(code: string): ImageErrorCode {
  switch (code) {
    case 'IMAGE_TOO_LARGE':
      return 'imageTooLarge';
    case 'UNSUPPORTED_TYPE':
      return 'unsupportedImageType';
    case 'MAX_COUNT_EXCEEDED':
      return 'maxImagesExceeded';
    case 'VISION_API_FAILED':
      return 'visionFailed';
    case 'STORAGE_FAILED':
      return 'storageFailed';
    default:
      return 'uploadFailed';
  }
}

/**
 * Ensures session is synced between server cookie and localStorage BEFORE upload.
 */
async function ensureSessionSync(): Promise<void> {
  try {
    const localSession = getOrCreateClientSession();

    const response = await fetch('/api/session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId: localSession }),
    });

    const data = await response.json();
    if (data.data?.sessionId) {
      saveSessionToStorage(data.data.sessionId);
    }
  } catch {
    console.warn('[session] Pre-upload session sync failed');
  }
}

/**
 * Options for image upload
 */
interface ImageUploadOptions {
  botId?: string | undefined;
}

/**
 * Uploads a single image to the API
 */
async function uploadImage(file: File, options?: ImageUploadOptions): Promise<ImageUploadResponse> {
  await ensureSessionSync();

  const formData = new FormData();
  formData.append('image', file);
  if (options?.botId) {
    formData.append('botId', options.botId);
  }

  // Story 6.5: Add referral attribution for new bot creation
  const isNewBot = !options?.botId;
  const referrerBotId = isNewBot ? getReferral() : null;
  if (referrerBotId) {
    formData.append('referrerBotId', referrerBotId);
  }

  const response = await fetch('/api/bots/upload-image', {
    method: 'POST',
    body: formData,
  });

  const data = await response.json();

  // Handle partial success (image uploaded but vision failed)
  if (response.status === 422 && data.data) {
    // Story 6.5: Clear referral after successful bot creation
    if (isNewBot && referrerBotId) {
      clearReferral();
    }
    return data as ImageUploadResponse;
  }

  if (!response.ok) {
    throw data as ImageUploadError;
  }

  // Story 6.5: Clear referral after successful bot creation
  if (isNewBot && referrerBotId) {
    clearReferral();
  }

  return data as ImageUploadResponse;
}

/**
 * Generates a unique ID for tracking uploaded images
 */
function generateImageId(): string {
  return `img-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

export interface UseImageUploadOptions {
  /** Bot ID to associate uploads with */
  botId?: string;
  /** Callback when an image upload completes successfully */
  onSuccess?: (image: UploadedImage) => void;
  /** Callback when an image upload fails */
  onError?: (image: UploadedImage, error: ImageUploadError) => void;
  /** Callback when vision analysis fails but image was stored */
  onPartialSuccess?: (image: UploadedImage) => void;
}

export interface UseImageUploadReturn {
  /** List of uploaded images with their status */
  images: UploadedImage[];
  /** Whether any upload is in progress */
  isUploading: boolean;
  /** Whether any image is being processed (vision analysis) */
  isProcessing: boolean;
  /** Upload images (validates and uploads each image) */
  uploadImages: (files: File[]) => void;
  /** Handle files rejected by dropzone */
  handleRejectedImages: (rejections: FileRejection[]) => void;
  /** Remove an image from the list by local ID */
  removeImage: (imageId: string) => void;
  /** Remove an image from the list by database contentId */
  removeByContentId: (contentId: string) => void;
  /** Retry a failed image upload */
  retryImage: (imageId: string) => void;
  /** Clear all images */
  clearImages: () => void;
  /** Total character count across all extracted images */
  totalCharacterCount: number;
  /** Bot ID created for image uploads (shared across all images) */
  botId: string | null;
  /** Number of images that can still be uploaded (based on MAX_IMAGE_COUNT) */
  remainingSlots: number;
  /** Whether max image count has been reached */
  isMaxReached: boolean;
}

/**
 * Hook for managing image uploads with TanStack Query
 *
 * Features:
 * - Client-side validation before upload (type, size, count)
 * - Progress tracking per image
 * - Optimistic updates for better UX
 * - Error handling with i18n support
 * - Partial success handling (image stored but vision failed)
 * - Retry functionality for failed images
 * - Max 5 images limit enforcement
 *
 * @example
 * ```tsx
 * const { images, isUploading, uploadImages, removeImage, retryImage } = useImageUpload({
 *   onSuccess: (image) => console.log('Processed:', image.fileName),
 *   onPartialSuccess: (image) => console.log('Stored but analysis failed:', image.fileName),
 * });
 *
 * return (
 *   <>
 *     <FileUploadZone
 *       onFilesAccepted={uploadImages}
 *       disabled={isUploading || isMaxReached}
 *     />
 *     <ImagePreviewList
 *       images={images}
 *       onRemove={removeImage}
 *       onRetry={retryImage}
 *     />
 *   </>
 * );
 * ```
 */
export function useImageUpload(options?: UseImageUploadOptions): UseImageUploadReturn {
  const queryClient = useQueryClient();
  const [images, setImages] = useState<UploadedImage[]>([]);
  const [createdBotId, setCreatedBotId] = useState<string | null>(options?.botId ?? null);
  // Store original files for retry functionality
  const [fileMap, setFileMap] = useState<Map<string, File>>(new Map());

  // Single image upload mutation
  const uploadMutation = useMutation({
    mutationFn: ({ file }: { file: File; imageId: string }) =>
      uploadImage(file, { botId: createdBotId ?? options?.botId }),
    onMutate: async ({ file, imageId }) => {
      // Create object URL for local preview
      const previewUrl = URL.createObjectURL(file);

      // Optimistic update - add image with uploading status
      const newImage: UploadedImage = {
        id: imageId,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
        status: 'uploading' as ImageStatus,
        previewUrl,
      };

      setImages((prev) => [...prev, newImage]);
    },
    onSuccess: (response, { imageId }) => {
      // Track the bot ID from the first successful upload
      if (response.data.botId && !createdBotId) {
        setCreatedBotId(response.data.botId);
      }

      // Check if this is a partial success (vision failed)
      const isPartialSuccess = response.data.status === 'error' || response.error;

      // Store the updated image data for the callback
      let updatedImageData: UploadedImage | null = null;

      // Update image with result and contentId
      setImages((prev) => {
        const updated = prev.map((img) => {
          if (img.id === imageId) {
            const updatedImage: UploadedImage = {
              ...img,
              status: isPartialSuccess ? ('error' as ImageStatus) : ('ready' as ImageStatus),
              contentId: response.data.contentId,
              thumbnailUrl: response.data.thumbnailUrl,
              extractedText: response.data.extractedText,
              description: response.data.description,
              preview: response.data.preview,
              ...(isPartialSuccess && { errorMessage: 'visionFailed' }),
            };
            updatedImageData = updatedImage;
            return updatedImage;
          }
          return img;
        });
        return updated;
      });

      // Invalidate content sources query to refresh the unified list
      if (response.data.botId) {
        queryClient.invalidateQueries({ queryKey: ['contentSources', response.data.botId] });
      }

      // Call appropriate callback after state update
      queueMicrotask(() => {
        if (updatedImageData) {
          if (isPartialSuccess) {
            options?.onPartialSuccess?.(updatedImageData);
          } else {
            // Clean up file from map on full success
            setFileMap((prev) => {
              const next = new Map(prev);
              next.delete(imageId);
              return next;
            });
            options?.onSuccess?.(updatedImageData);
          }
        }
      });
    },
    onError: (error: ImageUploadError, { imageId }) => {
      const errorKey = mapErrorCodeToI18nKey(error.error.code);

      // Store the failed image data for the callback
      let failedImageData: UploadedImage | null = null;

      setImages((prev) => {
        const updated = prev.map((img) => {
          if (img.id === imageId) {
            const updatedImage: UploadedImage = {
              ...img,
              status: 'error' as ImageStatus,
              errorMessage: errorKey,
            };
            failedImageData = updatedImage;
            return updatedImage;
          }
          return img;
        });
        return updated;
      });

      // Call onError after state update
      queueMicrotask(() => {
        if (failedImageData) {
          options?.onError?.(failedImageData, error);
        }
      });
    },
  });

  // Upload multiple images
  const uploadImages = useCallback(
    (newFiles: File[]) => {
      // Filter to only image files
      const imageFiles = newFiles.filter((f) => f.type.startsWith('image/'));

      // Check count limit
      const currentCount = images.length;
      const countValidation = validateImageCount(currentCount, imageFiles.length);

      if (!countValidation.isValid) {
        // Add error for excess images
        const allowedCount = MAX_IMAGE_COUNT - currentCount;
        const excessFiles = imageFiles.slice(allowedCount);

        excessFiles.forEach((file) => {
          const imageId = generateImageId();
          const errorImage: UploadedImage = {
            id: imageId,
            fileName: file.name,
            fileSize: file.size,
            mimeType: file.type,
            status: 'error',
            errorMessage: 'maxImagesExceeded',
          };
          setImages((prev) => [...prev, errorImage]);
        });

        // Only process allowed files
        imageFiles.splice(allowedCount);
      }

      imageFiles.forEach((file) => {
        // Validate image client-side first
        const validation = validateImage(file);

        if (!validation.isValid) {
          // Add image with error status
          const imageId = generateImageId();
          const errorImage: UploadedImage = {
            id: imageId,
            fileName: file.name,
            fileSize: file.size,
            mimeType: file.type,
            status: 'error',
            errorMessage: validation.errorCode ?? 'uploadFailed',
          };
          setImages((prev) => [...prev, errorImage]);
          return;
        }

        // Upload valid image
        const imageId = generateImageId();

        // Store file for potential retry
        setFileMap((prev) => {
          const next = new Map(prev);
          next.set(imageId, file);
          return next;
        });

        uploadMutation.mutate({ file, imageId });
      });
    },
    [images.length, uploadMutation]
  );

  // Handle files rejected by dropzone
  // Note: Uses image-specific error codes (imageTooLarge, unsupportedImageType, maxImagesExceeded)
  // which map to creator.imageUpload.error.* i18n keys
  const handleRejectedImages = useCallback((rejections: FileRejection[]) => {
    rejections.forEach((rejection) => {
      // Only handle image files
      if (!rejection.file.type.startsWith('image/')) {
        return;
      }

      const imageId = generateImageId();
      const errorCode = rejection.errors[0]?.code;

      // Map dropzone error codes to image-specific i18n keys
      let errorMessage: ImageErrorCode;
      if (errorCode === 'file-too-large') {
        errorMessage = 'imageTooLarge';
      } else if (errorCode === 'file-invalid-type') {
        errorMessage = 'unsupportedImageType';
      } else if (errorCode === 'too-many-files') {
        errorMessage = 'maxImagesExceeded';
      } else {
        errorMessage = 'uploadFailed';
      }

      const errorImage: UploadedImage = {
        id: imageId,
        fileName: rejection.file.name,
        fileSize: rejection.file.size,
        mimeType: rejection.file.type,
        status: 'error',
        errorMessage,
      };
      setImages((prev) => [...prev, errorImage]);
    });
  }, []);

  // Remove an image from the list and delete from database
  const removeImage = useCallback(
    async (imageId: string) => {
      // Find the image to get its contentId before removing
      const imageToRemove = images.find((img) => img.id === imageId);

      setImages((prev) => {
        const img = prev.find((i) => i.id === imageId);
        // Clean up object URL if it exists
        if (img?.previewUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(img.previewUrl);
        }
        return prev.filter((i) => i.id !== imageId);
      });

      // Clean up file from map
      setFileMap((prev) => {
        const next = new Map(prev);
        next.delete(imageId);
        return next;
      });

      // If image has a contentId, delete from database
      if (imageToRemove?.contentId && createdBotId) {
        try {
          await fetch(`/api/bots/${createdBotId}/content/${imageToRemove.contentId}`, {
            method: 'DELETE',
          });
          // Invalidate content sources to refresh unified panel
          queryClient.invalidateQueries({ queryKey: ['contentSources', createdBotId] });
        } catch (error) {
          console.error('[useImageUpload] Failed to delete content from API:', error);
        }
      }
    },
    [images, createdBotId, queryClient]
  );

  // Retry a failed image upload
  const retryImage = useCallback(
    (imageId: string) => {
      const file = fileMap.get(imageId);
      if (!file) {
        console.warn('[useImageUpload] Cannot retry - file not found for:', imageId);
        return;
      }

      // Update status to uploading and remove errorMessage
      setImages((prev) =>
        prev.map((img) => {
          if (img.id === imageId) {
            // Create a new object without errorMessage
            const { errorMessage, ...rest } = img;
            return { ...rest, status: 'uploading' as ImageStatus };
          }
          return img;
        })
      );

      // Create new upload mutation but reuse the imageId
      uploadMutation.mutate({ file, imageId });
    },
    [fileMap, uploadMutation]
  );

  // Remove an image by its database contentId (for syncing with unified panel)
  const removeByContentId = useCallback((contentId: string) => {
    setImages((prev) => {
      const imageToRemove = prev.find((img) => img.contentId === contentId);
      // Clean up object URL if it exists
      if (imageToRemove?.previewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(imageToRemove.previewUrl);
      }
      return prev.filter((img) => img.contentId !== contentId);
    });
  }, []);

  // Clear all images and reset
  const clearImages = useCallback(() => {
    // Clean up all object URLs
    images.forEach((img) => {
      if (img.previewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(img.previewUrl);
      }
    });
    setImages([]);
    setFileMap(new Map());
    setCreatedBotId(null);
  }, [images]);

  // Calculate total character count from extracted content
  const totalCharacterCount = images
    .filter((img) => img.status === 'ready' && img.preview)
    .reduce((acc, img) => acc + (img.preview?.length || 0), 0);

  // Calculate remaining slots
  const remainingSlots = Math.max(0, MAX_IMAGE_COUNT - images.length);
  const isMaxReached = images.length >= MAX_IMAGE_COUNT;

  // Check if any image is being processed
  const isProcessing = images.some(
    (img) => img.status === 'uploading' || img.status === 'processing'
  );

  return {
    images,
    isUploading: uploadMutation.isPending,
    isProcessing,
    uploadImages,
    handleRejectedImages,
    removeImage,
    removeByContentId,
    retryImage,
    clearImages,
    totalCharacterCount,
    botId: createdBotId,
    remainingSlots,
    isMaxReached,
  };
}

// Export types
export type { ImageUploadResponse, ImageUploadError };
